<script setup>
/**
 * Collision detection (HelpScout-style): shows the avatars of the OTHER agents who
 * currently have this conversation open. A green ring + "is replying" tooltip means
 * they are actively typing a reply; otherwise "is viewing". Rendered both on the
 * conversation list rows and at the top of the open conversation.
 */
import { computed } from 'vue';
import { useStore } from 'vuex';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
  size: {
    type: Number,
    default: 20,
  },
  max: {
    type: Number,
    default: 3,
  },
  placement: {
    type: String,
    default: 'top',
  },
});

const store = useStore();
const currentUserId = computed(() => store.getters.getCurrentUserID);

const typingUsers = computed(
  () =>
    store.getters['conversationTypingStatus/getUserList'](
      props.conversationId
    ) || []
);

const viewers = computed(() => {
  const records =
    store.getters['conversationViewers/getViewers'](props.conversationId) || [];
  const seen = new Set();
  return records
    .filter(user => {
      if (!user || user.id === currentUserId.value) return false;
      if (seen.has(user.id)) return false;
      seen.add(user.id);
      return true;
    })
    .map(user => {
      const isReplying = typingUsers.value.some(
        typer => typer.id === user.id && (typer.type === 'user' || !typer.type)
      );
      return {
        id: user.id,
        name: user.available_name || user.name,
        thumbnail: user.thumbnail || user.avatar_url,
        isReplying,
        state: isReplying ? 'replying' : 'viewing',
      };
    });
});

const visibleViewers = computed(() => viewers.value.slice(0, props.max));
const overflowCount = computed(() =>
  Math.max(viewers.value.length - props.max, 0)
);
</script>

<template>
  <div
    v-if="viewers.length"
    class="flex items-center -space-x-1.5 rtl:space-x-reverse"
  >
    <span
      v-for="viewer in visibleViewers"
      :key="viewer.id"
      v-tooltip="{ content: `${viewer.name} is ${viewer.state}`, placement }"
      class="inline-flex rounded-full ring-2 transition-colors"
      :class="viewer.isReplying ? 'ring-n-teal-9' : 'ring-n-background'"
    >
      <Avatar
        :name="viewer.name"
        :src="viewer.thumbnail"
        :size="size"
        hide-offline-status
      />
    </span>
    <span
      v-if="overflowCount"
      v-tooltip="{ content: `+${overflowCount} more viewing`, placement }"
      class="inline-flex items-center justify-center font-medium rounded-full ring-2 bg-n-slate-4 text-n-slate-11 ring-n-background"
      :style="{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${Math.round(size * 0.45)}px`,
      }"
    >
      +{{ overflowCount }}
    </span>
  </div>
</template>
